import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import Button from "@/components/button"
import Bottombar from "@/components/bottombar"
import RadioGroup, { RadioGroupOption } from "@/components/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { BreakdownFormData, BreakdownType } from "@/lib/form/types"
import * as y from "yup"
import {
  useFieldStatus,
  useForm,
  UseFormReturn,
  useWatch,
} from "@/lib/form/form"
import { createUuid } from "@/lib/utils"
import { useEffect, useState } from "react"
import { createValidator } from "@/lib/validators/limit"
import { toPath } from "lodash"
import { toast } from "sonner"

export type MachineRemarksSheetProps = {
  form: UseFormReturn
}

export const issues: RadioGroupOption[] = [
  {
    label: "Break Down",
    value: BreakdownType.BREAK_DOWN,
  },
  {
    label: "Change Over",
    value: BreakdownType.CHANGE_OVER,
  },
  {
    label: "Power Failure",
    value: BreakdownType.POWER_FAILURE,
  },
  {
    label: "Other",
    value: BreakdownType.OTHER,
  },
]

type RemarksFormData = {
  remark: string
  issue: string
}

export default function MachineRemarksSheet({
  form,
}: MachineRemarksSheetProps) {
  const remarksForm = useForm()
  const { register, setValue, handleSubmit, reset } = remarksForm
  const [open, setOpen] = useState(false)
  const [path, setPath] = useState("")
  const status = useFieldStatus({
    form: remarksForm,
  })
  const remark = useWatch({
    form: remarksForm,
    name: "remark",
  })

  useInit()

  function useInit() {
    useEffect(() => {
      register("issue", {
        validate: createValidator(y.string().ensure().required()),
      })
      register("remark", {
        validate: createValidator(y.string().min(1).required()),
      })

      form.addEventHandler("clickMachineRemarks", (__event, ctx) => {
        setPath(ctx.path)
        setOpen(true)
      })
    }, [form])

    useEffect(() => {
      if (!open) {
        setTimeout(() => {
          reset()
        }, 1000)
      }
    }, [open, reset])
  }

  async function handleChange(path: string, value: any) {
    setValue(path, value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
    await remarksForm.runAllValidators()
  }

  const [isSubmitting, setIsSubmitting] = useState(false)

  async function submit(data: Record<string, any>) {
    const formData = data as RemarksFormData

    const breakdownPath = `${path}.breakdown`
    const breakdowns: BreakdownFormData[] = form.getValues(breakdownPath) || []

    if (breakdowns.length) {
      const lastBreakdown = breakdowns.at(-1)

      if (!lastBreakdown?.endedAt) {
        close()

        return
      }
    }

    // Extract indices from the path (e.g., "products[0].productionLines[1].machines[2]")
    const pathParts = toPath(path)
    const prodIdx = parseInt(pathParts[1], 10)
    const lineIdx = parseInt(pathParts[3], 10)

    const breakdown: BreakdownFormData = {
      id: createUuid(),
      type: formData.issue as BreakdownType,
      remark: formData.remark || "",
      startedAt: new Date().toISOString(),
      endedAt: "",
    }

    const machineData = form.getValues(path)
    if (!machineData) {
      toast.error("Stage data not found. Please try again.")
      return
    }

    setIsSubmitting(true)

    try {
      const breakdowns: BreakdownFormData[] =
        form.getValues(breakdownPath) || []
      form.setValue(breakdownPath, [...breakdowns, breakdown], {
        shouldDirty: true,
        shouldTouch: true,
      })

      const linePath = `products[${prodIdx}].productionLines[${lineIdx}]`
      form.setValue(linePath, form.getValues(linePath), {
        shouldDirty: true,
      })

      close()
    } catch (err) {
      console.error("Failed to store breakdown locally:", err)
      toast.error("Failed to save breakdown locally. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  function close() {
    setOpen(false)
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="bottom"
        title="Fill remarks on stage"
        className="shadow-drop-shadow rounded-t-lg"
      >
        <form onSubmit={handleSubmit(submit)}>
          <SheetHeader>
            <SheetTitle className="text-xl">Fill Remarks on Stage</SheetTitle>
          </SheetHeader>

          <div className="px-4 pb-4">
            <RadioGroup
              label="Select issue"
              options={issues}
              onChange={(value) => handleChange("issue", value)}
            />
            <Textarea
              className="mt-6"
              placeholder="Describe the issue and action taken"
              value={remark}
              onChange={(value) => handleChange("remark", value)}
            />
          </div>

          <Bottombar className="flex gap-4 bg-white">
            <Button variant="outline" onClick={close} disabled={isSubmitting}>
              Back
            </Button>
            <Button
              variant="default"
              className="flex-1"
              type="submit"
              disabled={status !== "VALID" || isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </Bottombar>
        </form>
      </SheetContent>
    </Sheet>
  )
}
